import React, { useMemo, useCallback } from 'react';
import { Table, TableColumnType, Tag, Button, Popconfirm, message } from 'antd';
import dayjs from 'dayjs';
import { VipSuppIdRecord, VipSuppIdStatus } from '../types';
import { useAtomValue } from 'jotai';
import { isMobileAtom } from '@/jotai/atoms/userAtom';
import { deleteVipSuppId } from '../api';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '../constants';

interface VipSuppIdTableProps {
  data: VipSuppIdRecord[];
  loading: boolean;
  pagination: {
    current: number;
    pageSize: number;
    total: number;
    onChange: (page: number, pageSize?: number) => void;
  };
  onEdit: (record: VipSuppIdRecord) => void;
}

const VipSuppIdTable: React.FC<VipSuppIdTableProps> = ({
  data,
  loading,
  pagination,
  onEdit,
}) => {
  const isMobile = useAtomValue(isMobileAtom);
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: ({ id, beautifulNumber }: { id: number; beautifulNumber: number }) =>
      deleteVipSuppId(id, beautifulNumber),
    onSuccess: () => {
      message.success('删除成功');
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.VIP_SUPPID_LIST] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.VIP_SUPPID_STATS] });
    },
    onError: (error: Error) => {
      message.error(error.message || '删除失败');
    },
  });

  const handleDelete = useCallback((id: number, beautifulNumber: number | string) => {
    // 确保 beautifulNumber 是数字类型
    const beautifulNumberNum = typeof beautifulNumber === 'string' 
      ? Number(beautifulNumber) 
      : beautifulNumber;
    
    if (isNaN(beautifulNumberNum)) {
      message.error('VIP SUPPID 格式错误');
      return;
    }
    
    deleteMutation.mutate({ id, beautifulNumber: beautifulNumberNum });
  }, [deleteMutation]);

  const columns = useMemo<TableColumnType<VipSuppIdRecord>[]>(
    () => [
      {
        title: '序号',
        key: 'index',
        width: 80,
        render: (_: any, __: any, index: number) => {
          return (pagination.current - 1) * pagination.pageSize + index + 1;
        },
      },
      {
        title: 'VIP SUPPID',
        dataIndex: 'beautifulNumber',
        key: 'beautifulNumber',
        width: 150,
        render: (_: any, record: VipSuppIdRecord) => {
          if (record.beautifulNumber !== undefined && record.beautifulNumber !== null) {
            return String(record.beautifulNumber);
          }
          if (record.suppId) {
            return record.suppId;
          }
          return '-';
        },
      },
      {
        title: '状态',
        dataIndex: 'status',
        key: 'status',
        width: 100,
        render: (status: VipSuppIdStatus) => {
          return (
            <Tag color={status === VipSuppIdStatus.OCCUPIED ? 'success' : 'default'}>
              {status === VipSuppIdStatus.OCCUPIED ? '已占用' : '未占用'}
            </Tag>
          );
        },
      },
      {
        title: '更新时间',
        dataIndex: 'updateTime',
        key: 'updateTime',
        width: 180,
        render: (time: string) =>
          time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-',
      },
      {
        title: '更新人',
        dataIndex: 'updateUserName',
        key: 'updateUserName',
        width: 120,
        render: (name: string) => name || '-',
      },
      {
        title: '操作',
        key: 'action',
        width: 150,
        fixed: isMobile ? 'right' : undefined,
        render: (_: any, record: VipSuppIdRecord) => {
          // 如果状态是已占用，则不展示编辑和删除
          if (record.status === VipSuppIdStatus.OCCUPIED) {
            return <span style={{ color: '#999' }}>-</span>;
          }

          // 只有未占用状态才显示编辑和删除按钮
          return (
            <div style={{ display: 'flex', gap: 8 }}>
              <Button type="link" size="small" onClick={() => onEdit(record)}>
                编辑
              </Button>
              <Popconfirm
                title="确定要删除吗？"
                onConfirm={() => handleDelete(record.id, record.beautifulNumber)}
                okText="确定"
                cancelText="取消"
              >
                <Button type="link" size="small" danger>
                  删除
                </Button>
              </Popconfirm>
            </div>
          );
        },
      },
    ],
    [pagination, isMobile, onEdit, deleteMutation]
  );

  return (
    <Table<VipSuppIdRecord>
      columns={columns}
      dataSource={data}
      rowKey="id"
      loading={loading}
      scroll={{ x: isMobile ? 800 : undefined }}
      pagination={{
        current: pagination.current,
        pageSize: pagination.pageSize,
        total: pagination.total,
        onChange: pagination.onChange,
        onShowSizeChange: pagination.onChange,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total) => `共 ${total} 条`,
        pageSizeOptions: ['10', '20', '50', '100'],
      }}
      size={isMobile ? 'small' : 'middle'}
    />
  );
};

export default VipSuppIdTable;

